import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { t } from 'i18next'

import { ApiError } from '@/api/api-error'
import type { ApiErrorData } from '@/api/http.api'
import {
  login,
  logout as logoutApi,
  LoginRequest,
  LoginResponse,
  LoginUnionResponse,
  MfaEnableResponse,
  MfaRegenerateResponse,
  MfaRegenerateRequest,
  MfaSetupResponse,
  EmailMfaSendResponse,
  MfaEmailEnableResponse,
  MethodChangeConfirmResponse,
  MethodChangeConfirmRequest,
  confirmMethodChange,
  enableEmailMfa,
  ResetPasswordRequest,
  resetPassword,
  verifyMfa,
  initTotpSetup,
  enableTotp,
  regenerateBackupCodes,
  sendEmailMfaCode,
} from '@app/api/auth.api'
import { acceptConsent, AcceptConsentPayload } from '@app/api/consent.api'
import { getProfileData, setProfileUser } from '@app/store/slices/profile-slice'
import { clearAuthFlag, deleteUser, setAuthFlag, readAuthFlag } from '@app/store/services/local-storage.service'
import { notify } from '@/controllers/toast-bridge'
import { getErrorMessage } from '@app/utils/utils'
import { resetStore } from '@app/store/actions/reset'

type MfaPendingState = {
  type: 'LOGIN' | 'SETUP' | null
  mfaToken: string | null
  emailMfaToken: string | null
  setupToken: string | null
  method: string | null
  availableMethods: string[]
}

const initialMfaPending: MfaPendingState = {
  type: null,
  mfaToken: null,
  emailMfaToken: null,
  setupToken: null,
  method: null,
  availableMethods: [],
}

export type AuthSlice = {
  isAuthenticated: boolean
  isLoading: boolean
  error?: string | undefined
  consentRequired: boolean
  mfaPending: MfaPendingState
}

const initialState: AuthSlice = {
  isLoading: false,
  error: '',
  isAuthenticated: readAuthFlag(),
  consentRequired: false,
  mfaPending: initialMfaPending,
}

export const doLogin = createAsyncThunk<LoginUnionResponse, LoginRequest>(
  'auth/doLogin',
  async (loginPayload: LoginRequest, { rejectWithValue }) => {
    try {
      return await login(loginPayload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doMfaVerify = createAsyncThunk<LoginResponse, { code: string; method: 'TOTP' | 'EMAIL' | 'BACKUP_CODE' }>(
  'auth/doMfaVerify',
  async ({ code, method }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthSlice }
      const { mfaToken, emailMfaToken } = state.auth.mfaPending
      const token = method === 'EMAIL' ? emailMfaToken : mfaToken
      if (!token) throw new Error('No MFA session')
      const res = await verifyMfa({ mfaToken: token, method, code })
      dispatch(getProfileData(res.id))
      setAuthFlag()
      setProfileUser({ id: res.id })
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doSendEmailMfaCode = createAsyncThunk<EmailMfaSendResponse, string | undefined>(
  'auth/doSendEmailMfaCode',
  async (language, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthSlice }
      const { mfaToken, emailMfaToken } = state.auth.mfaPending
      const authToken = emailMfaToken ?? mfaToken
      if (!authToken) throw new Error('No MFA session')
      return await sendEmailMfaCode({ mfaToken: authToken, language })
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doSendEmailMfaForSetup = createAsyncThunk<EmailMfaSendResponse, string | undefined>(
  'auth/doSendEmailMfaForSetup',
  async (language, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthSlice }
      const { setupToken } = state.auth.mfaPending
      if (!setupToken) throw new Error('No setup token')
      return await sendEmailMfaCode({ mfaToken: setupToken, language })
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doEnableEmailMfa = createAsyncThunk<MfaEmailEnableResponse, { emailMfaToken: string; code: string }>(
  'auth/doEnableEmailMfa',
  async ({ emailMfaToken, code }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthSlice }
      const { setupToken } = state.auth.mfaPending
      if (!setupToken) throw new Error('No setup token')
      const res = await enableEmailMfa({ setupToken, emailMfaToken, code })
      dispatch(getProfileData(res.id))
      setAuthFlag()
      setProfileUser({ id: res.id })
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doMfaSetup = createAsyncThunk<MfaSetupResponse, void>(
  'auth/doMfaSetup',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthSlice }
      const setupToken = state.auth.mfaPending.setupToken
      if (!setupToken) throw new Error('No setup token')
      return await initTotpSetup({ setupToken })
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doMfaEnable = createAsyncThunk<MfaEnableResponse, string>(
  'auth/doMfaEnable',
  async (code, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthSlice }
      const setupToken = state.auth.mfaPending.setupToken
      if (!setupToken) throw new Error('No setup token')
      const res = await enableTotp({ setupToken, code })
      dispatch(getProfileData(res.id))
      setAuthFlag()
      setProfileUser({ id: res.id })
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doConfirmMethodChange = createAsyncThunk<MethodChangeConfirmResponse, MethodChangeConfirmRequest>(
  'auth/doConfirmMethodChange',
  async (payload, { rejectWithValue }) => {
    try {
      return await confirmMethodChange(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doMfaRegenerateBackupCodes = createAsyncThunk<MfaRegenerateResponse, MfaRegenerateRequest>(
  'auth/doMfaRegenerateBackupCodes',
  async (payload, { rejectWithValue }) => {
    try {
      return await regenerateBackupCodes(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doLogout = createAsyncThunk('auth/doLogout', async (_, { dispatch }) => {
  const wasLoggedIn = readAuthFlag()

  if (wasLoggedIn) {
    await logoutApi().catch(() => {
      // Fail-open: local logout proceeds even if backend call fails
    })
  }

  clearAuthFlag()
  deleteUser()
  dispatch(resetStore())

  if (wasLoggedIn) {
    notify.success(t('beNotifications.success.logoutSuccess'))
  }
})

/** Klucz komunikatu nieprzyjętej prośby o reset hasła. Limit liczy się po adresie, zanim backend
 *  sprawdzi konto, a serwis odpowiada sukcesem także dla adresu bez konta — rozróżnienie
 *  przypadków nie ujawnia więc, czy konto istnieje. */
const RESET_MAIL_UNAVAILABLE = 'beNotifications.errors.passwordResetMailUnavailable'

const getResetPasswordErrorKey = (error: unknown): string => {
  if (error instanceof ApiError && error.status === 429) return 'resetPassword.rateLimited'

  // Klucz z treści odpowiedzi, nie sam status 503 — ten zwraca też Cloud Run, gdy backend nie odpowiada.
  if (error instanceof ApiError && (error.options as ApiErrorData | undefined)?.message === RESET_MAIL_UNAVAILABLE) {
    return RESET_MAIL_UNAVAILABLE
  }

  // Brak odpowiedzi nie przesądza o odrzuceniu — serwer mógł przyjąć prośbę przed zerwaniem połączenia.
  if (getErrorMessage(error) === 'common.connectionError') return 'resetPassword.requestUnconfirmed'

  return 'resetPassword.requestFailed'
}

export const doResetPassword = createAsyncThunk(
  'auth/doResetPassword',
  async (resetPassPayload: ResetPasswordRequest, { rejectWithValue }) => {
    try {
      return await resetPassword(resetPassPayload)
    } catch (error) {
      return rejectWithValue(getResetPasswordErrorKey(error))
    }
  },
)

export const doAcceptConsent = createAsyncThunk(
  'auth/doAcceptConsent',
  async (payload: AcceptConsentPayload, { rejectWithValue }) => {
    try {
      return await acceptConsent(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    requireConsent(state) {
      state.consentRequired = true
    },
    clearMfaPending(state) {
      state.mfaPending = initialMfaPending
    },
  },
  extraReducers: (builder) => {
    builder.addCase(doLogin.pending, (state) => {
      state.error = undefined
      state.isLoading = true
    })
    builder.addCase(doLogin.fulfilled, (state, action) => {
      const payload = action.payload

      if ('mfaSetupRequired' in payload) {
        state.mfaPending = {
          type: 'SETUP',
          mfaToken: null,
          emailMfaToken: null,
          setupToken: payload.setupToken,
          method: null,
          availableMethods: [],
        }
      } else if ('mfaRequired' in payload) {
        state.mfaPending = {
          type: 'LOGIN',
          mfaToken: payload.mfaToken,
          emailMfaToken: null,
          setupToken: null,
          method: payload.method,
          availableMethods: payload.availableMethods ?? [],
        }
      } else if ('id' in payload) {
        setAuthFlag()
        state.isAuthenticated = true
        state.consentRequired = !!payload.consentRequired
      }
      state.isLoading = false
    })
    builder.addCase(doLogin.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    builder.addCase(doMfaVerify.pending, (state) => {
      state.isLoading = true
      state.error = undefined
    })
    builder.addCase(doMfaVerify.fulfilled, (state, action) => {
      state.isAuthenticated = true
      state.consentRequired = !!action.payload.consentRequired
      state.mfaPending = initialMfaPending
      state.isLoading = false
    })
    builder.addCase(doMfaVerify.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    builder.addCase(doMfaEnable.fulfilled, (state, action) => {
      state.isAuthenticated = true
      state.consentRequired = !!action.payload.consentRequired
      state.mfaPending = initialMfaPending
    })

    builder.addCase(doSendEmailMfaCode.fulfilled, (state, action) => {
      state.mfaPending.emailMfaToken = action.payload.emailMfaToken
    })

    builder.addCase(doEnableEmailMfa.fulfilled, (state, action) => {
      state.isAuthenticated = true
      state.consentRequired = !!action.payload.consentRequired
      state.mfaPending = initialMfaPending
      state.isLoading = false
    })

    builder.addCase(doConfirmMethodChange.fulfilled, (state, action) => {
      state.mfaPending = {
        type: 'SETUP',
        mfaToken: null,
        emailMfaToken: null,
        setupToken: action.payload.setupToken,
        method: null,
        availableMethods: [],
      }
    })

    builder.addCase(doLogout.fulfilled, (state) => {
      state.isAuthenticated = false
      state.consentRequired = false
      state.mfaPending = initialMfaPending
    })
    builder.addCase(doAcceptConsent.fulfilled, (state) => {
      state.consentRequired = false
    })

    builder.addCase(resetStore, () => initialState)
  },
})

export default authSlice.reducer
export const { requireConsent, clearMfaPending } = authSlice.actions
